import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { store } from 'components/Store';
import { useGetUserHash } from 'components/useTrackingCodes';
import { consultantAgenciesMembers } from 'components/Dashboard/SidebarElements/parliamentarianUsers';
import getActivePlan from 'components/Settings/Utilities/getActivePlan';
import useGetAccessToken from 'components/Dashboard/apiCalls/useGetAccessToken';
import getCurrentTeam, { useGetCurrentTeam } from 'components/Settings/Team/getCurrentTeam';
import getUrlParam from 'components/Dashboard/utilities/getUrlParam';
import useClientsFunctions from 'components/Dashboard/hooks/useClientsFunctions';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import useChangeClient from 'components/Dashboard/Navigation/useChangeClient';
import useGetRemainingCredits from 'components/Dashboard/hooks/useGetRemainingCredits';
import setParamsCode from 'components/Dashboard/MainSearchBar/setParamsCode';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import { loadEmailAlerts } from 'components/Dashboard/utilities/loadKeyWords';
import usePushNotifications from 'components/Settings/EmailAlerts/usePushNotifications';
import {
  isServiceWorkerSupported,
  getServiceWorkerRegistration,
  registerServiceWorker,
} from 'components/Settings/EmailAlerts/pushServiceWorkerUtils';
const intercomConditional =
  window.Intercom &&
  !!import.meta.env.VITE_APP_INTERCOM_APP_ID &&
  `${import.meta.env.VITE_APP_INTERCOM_APP_ID}`.trim() !== '';

const useAppFunctions = () => {
  const globalState = useContext(store);
  const { state, dispatch } = globalState;
  const { activePlan, clients, team } = state;
  const { code } = activePlan ?? {};

  const [isLoading, setIsLoading] = useState(true);

  const { isAuthenticated, user, logout } = useAuth0();
  const { getAccessToken } = useGetAccessToken();
  const { getUserHash } = useGetUserHash();
  const { setCurrentTeam, getTeams } = useClientsFunctions();
  const { newAgencyPlan } = useTopicOrClientWord();
  const { changeClientFunction } = useChangeClient();
  const { getRemainingCredits } = useGetRemainingCredits();
  const { currentTeamCall } = useGetCurrentTeam();
  const { generalApiCall } = useGeneralApiCall();
  const { subscribePushNotifications, unsubscribePushNotifications } = usePushNotifications();

  const location = useLocation();
  const navigate = useNavigate();

  const updateViewQuery = useRef(null);
  const switchTeamIdParam = getUrlParam('switch-team-id');
  const logoutPage = location.pathname === '/logout';

  const switchClientCondition =
    newAgencyPlan &&
    switchTeamIdParam &&
    parseFloat(switchTeamIdParam) !== team.teamId &&
    clients?.find((client) => client?.teamId === parseFloat(switchTeamIdParam));

  updateViewQuery.current = () => {
    const urlParams = new URLSearchParams(location.search);
    if (!!activePlan && Object.keys(activePlan).length > 0 && !location.pathname.startsWith('/settings')) {
      setParamsCode({ urlParams, code });
      navigate(
        {
          search: urlParams.toString(),
          hash: window.location.hash,
        },
        {
          replace: true,
          state: location?.state,
        }
      );
    }
  };

  const callActivePlan = useRef(null);
  callActivePlan.current = async (source) => {
    const pathnamesToExclude = ['/login', '/register', '/login-success', '/initialise', '/accept-invite'];
    const isShareDocumentPage = location.pathname.startsWith('/share-document');
    try {
      if (!logoutPage) {
        //INTERCOM
        if (isAuthenticated && !pathnamesToExclude?.includes(location?.pathname) && !isShareDocumentPage) {
          if (intercomConditional) {
            let hmac = await getUserHash(source);
            if (hmac?.status === 401) {
              return logout({ logoutParams: { returnTo: window.location.origin } });
            }
            if (!!hmac) {
              window.Intercom('boot', {
                app_id: `${import.meta.env.VITE_APP_INTERCOM_APP_ID}`,
                name: user.name,
                email: user.email,
                user_id: user.sub,
                custom_launcher_selector: '.contact-button-launch-messenger',
                user_hash: hmac,
              });
            }
          }
          let activePlan = await getActivePlan(getAccessToken, source, logout);
          if (!!activePlan) {
            const newAgencyPlan = consultantAgenciesMembers(activePlan?.code) && activePlan?.allowMultipleTeams;
            let currentTeamCompleteCall = await currentTeamCall({ source });
            let remainingCredits = await getRemainingCredits({ source });
            if (typeof remainingCredits !== 'undefined') {
              dispatch({ type: 'MODIFY_SECTION', parameter: 'remainingCredits', value: remainingCredits });
            }
            if (
              activePlan?.userOnboarded &&
              currentTeamCompleteCall?.status === 204 &&
              !window.location.pathname.startsWith('/settings')
            ) {
              return window.location.assign('/settings/clients');
            } else if (currentTeamCompleteCall?.status !== 500) {
              let currentTeam = currentTeamCompleteCall?.data;
              if (!!switchTeamIdParam) {
                if (newAgencyPlan) {
                  const teamId = parseFloat(switchTeamIdParam);
                  const clients = await getTeams({ includeKeywordsList: false, source });
                  const switchClientCondition =
                    teamId !== currentTeam.teamId && clients?.find((client) => client?.teamId === teamId);

                  if (switchClientCondition) {
                    await setCurrentTeam({ teamId: teamId });
                    //AE: We need to call again this information with new team
                    currentTeam = await getCurrentTeam(getAccessToken, source);
                    activePlan = await getActivePlan(getAccessToken, source, logout);
                  }
                }
                const params = new URLSearchParams(location.search);
                params.delete('switch-team-id');
                navigate({ search: params.toString() }, { replace: true });
              }
              dispatch({ type: 'MODIFY_SECTION', parameter: 'team', value: currentTeam });
              dispatch({ type: 'MODIFY_SECTION', parameter: 'activePlan', value: activePlan });
              setIsLoading(false);
            }
          }
        } else {
          if (intercomConditional && !isShareDocumentPage) {
            window.Intercom('boot', {
              app_id: `${import.meta.env.VITE_APP_INTERCOM_APP_ID}`,
              custom_launcher_selector: '.contact-button-launch-messenger',
            });
          }
          setIsLoading(false);
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {}
  };

  const handleswitchTeamId = useRef(null);
  handleswitchTeamId.current = async () => {
    if (switchClientCondition) {
      await changeClientFunction({ clientId: parseFloat(switchTeamIdParam) });
    }
    if (switchTeamIdParam) {
      const params = new URLSearchParams(location.search);
      params.delete('switch-team-id');
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  };

  useEffect(() => {
    if (!isLoading) {
      handleswitchTeamId.current();
    }
  }, [switchTeamIdParam, switchClientCondition]);

  const pushNotificationsValidations = async () => {
    const pathnamesToExclude = ['/login', '/register', '/login-success', '/initialise', '/accept-invite'];
    if (!logoutPage && isAuthenticated && !pathnamesToExclude?.includes(location?.pathname)) {
      try {
        if (!isServiceWorkerSupported() || !('PushManager' in window) || !('Notification' in window)) return;
        let alertSettings;
        if (newAgencyPlan) {
          const results = await generalApiCall({
            pathname: '/api/alert-settings/all-teams',
            method: 'get',
            needsAuthentication: true,
            notShowErrorMessage: true,
          });
          alertSettings = results?.flatMap((item) => item.alertSettings) ?? [];
        } else {
          alertSettings = (await loadEmailAlerts(getAccessToken)) ?? [];
        }

        const hasPushNotificationAlert = alertSettings.some((alert) =>
          alert.alertTypes?.split(', ').includes('PushNotification')
        );
        if (!hasPushNotificationAlert) return;

        const existingReg = await getServiceWorkerRegistration();
        if (existingReg) return;

        if (Notification.permission === 'granted') {
          await unsubscribePushNotifications();
          await subscribePushNotifications();
        } else {
          alert('permission requested');
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            await subscribePushNotifications();
          }
        }
      } catch (error) {
        console.error('registerPushServiceWorker error:', error);
      }
    }
  };

  useEffect(() => {
    const source = new AbortController();
    callActivePlan.current(source);
    pushNotificationsValidations();
    return () => {
      source.abort('Main app loading cancelled by the user');
    };
  }, []);

  //AE: This replaces the scrollToTop element
  useEffect(() => {
    window.scrollTo(0, 0);
    const paths = ['/', '/pricing', '/data-hub'];
    if (!isAuthenticated && paths.some((path) => location.pathname === path)) {
      document.querySelector('body').classList.add('body-home-page');
    }
    return () => {
      document.querySelector('body').classList.remove('body-home-page');
    };
  }, [location?.pathname]);

  useEffect(() => {
    setTimeout(() => {
      updateViewQuery.current();
    }, 10);
  }, [activePlan, location.pathname]);

  return { isLoading, switchClientCondition, pushNotificationsValidations };
};

export { intercomConditional };
export default useAppFunctions;
